// ============================================================================
// useDateRangePreset — shared date-range preset resolver
// ============================================================================
// Centralises the dashboard/report date filter logic so every screen has the
// same options and cold-load default. Default is MTD (per spec).

import { useCallback, useMemo, useState } from 'react';
import {
  startOfDay, endOfDay,
  startOfMonth, endOfMonth,
  startOfYear,
  subDays, subMonths, format,
} from 'date-fns';

export type DateRangePreset =
  | 'TODAY'
  | 'YESTERDAY'
  | 'LAST_7_DAYS'
  | 'LAST_30_DAYS'
  | 'MTD'
  | 'LAST_MONTH'
  | 'YTD'
  | 'CUSTOM';

export interface DateRangeValue {
  preset: DateRangePreset;
  /** ISO date `YYYY-MM-DD` (server-side timezone). */
  from: string;
  /** ISO date `YYYY-MM-DD`. */
  to: string;
}

export const PRESET_OPTIONS: { value: DateRangePreset; label: string }[] = [
  { value: 'TODAY',         label: 'Today' },
  { value: 'YESTERDAY',     label: 'Yesterday' },
  { value: 'LAST_7_DAYS',   label: 'Last 7 Days' },
  { value: 'LAST_30_DAYS',  label: 'Last 30 Days' },
  { value: 'MTD',           label: 'Month To Date' },
  { value: 'LAST_MONTH',    label: 'Last Month' },
  { value: 'YTD',           label: 'Year To Date' },
  { value: 'CUSTOM',        label: 'Custom' },
];

const fmt = (d: Date) => format(d, 'yyyy-MM-dd');

/**
 * Resolves a preset to a concrete `{from, to}` window using the current date.
 * For `CUSTOM`, callers provide the dates; the resolver passes them through.
 */
export function resolvePreset(preset: DateRangePreset, customFrom?: string, customTo?: string): { from: string; to: string } {
  const today = startOfDay(new Date());
  switch (preset) {
    case 'TODAY':         return { from: fmt(today), to: fmt(endOfDay(today)) };
    case 'YESTERDAY': {
      const y = subDays(today, 1);
      return { from: fmt(y), to: fmt(endOfDay(y)) };
    }
    case 'LAST_7_DAYS':   return { from: fmt(subDays(today, 6)),  to: fmt(endOfDay(today)) };
    case 'LAST_30_DAYS':  return { from: fmt(subDays(today, 29)), to: fmt(endOfDay(today)) };
    case 'MTD':           return { from: fmt(startOfMonth(today)), to: fmt(endOfDay(today)) };
    case 'LAST_MONTH': {
      const lm = subMonths(today, 1);
      return { from: fmt(startOfMonth(lm)), to: fmt(endOfMonth(lm)) };
    }
    case 'YTD':           return { from: fmt(startOfYear(today)), to: fmt(endOfDay(today)) };
    case 'CUSTOM':        return {
      from: customFrom ?? fmt(today),
      to:   customTo   ?? fmt(today),
    };
  }
}

/**
 * State hook. Cold default is MTD. Switching preset auto-resolves the window;
 * picking CUSTOM exposes editable from/to that the consumer can update via
 * setCustomFrom / setCustomTo.
 */
export function useDateRangePreset(initialPreset: DateRangePreset = 'MTD'): {
  value: DateRangeValue;
  setPreset: (p: DateRangePreset) => void;
  setCustomFrom: (date: string) => void;
  setCustomTo: (date: string) => void;
} {
  const initial = resolvePreset(initialPreset);
  const [preset, setPresetState] = useState<DateRangePreset>(initialPreset);
  const [customFrom, setCustomFromState] = useState<string>(initial.from);
  const [customTo, setCustomToState] = useState<string>(initial.to);

  const setPreset = useCallback((p: DateRangePreset) => {
    setPresetState(p);
    if (p !== 'CUSTOM') {
      const r = resolvePreset(p);
      setCustomFromState(r.from);
      setCustomToState(r.to);
    }
  }, []);

  const value = useMemo<DateRangeValue>(() => {
    if (preset === 'CUSTOM') return { preset, from: customFrom, to: customTo };
    const r = resolvePreset(preset);
    return { preset, from: r.from, to: r.to };
  }, [preset, customFrom, customTo]);

  return { value, setPreset, setCustomFrom: setCustomFromState, setCustomTo: setCustomToState };
}
